import { Fragment, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Loader, NumberInput, SegmentedControl, Select, TextInput } from '@mantine/core'
import { getAllUsers, getSlotMatch } from './helper'
import { DisplayZone, formatShortDay, formatTime, providerLabel, sourceColor, zoneLabel } from './calendarFormat'

interface IProps {
  clientId: string
  clientName?: string
}

const severityStyle: Record<string, { color: string; label: string }> = {
  blocker: { color: 'red', label: 'Why no slots' },
  warning: { color: 'orange', label: 'Warning' },
  ok: { color: 'green', label: 'Available' },
  info: { color: 'gray', label: 'Note' },
}

const pad = (n: number) => String(n).padStart(2, '0')
const todayKey = () => {
  const d = new Date()
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const dayTime = (iso: string, zone: DisplayZone | null) => `${formatShortDay(iso, zone)}, ${formatTime(iso, zone)}`

const SlotMatchChecker: React.FC<IProps> = ({ clientId, clientName }) => {
  const [coaches, setCoaches] = useState<{ value: string; label: string }[]>([])
  const [coachId, setCoachId] = useState<string | null>(null)
  const [date, setDate] = useState(todayKey())
  const [duration, setDuration] = useState<number | ''>(60)
  const [bookedBy, setBookedBy] = useState<'client' | 'coach'>('client')
  const [result, setResult] = useState<any>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    getAllUsers().then((res) =>
      setCoaches(
        (res.data || [])
          .filter((u: any) => u.isProvider && u.uuid !== clientId)
          .map((u: any) => ({ value: u.uuid, label: `${u.displayName || 'Unnamed'} — ${u.email}` }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label))
      )
    )
  }, [clientId])

  const canCheck = !!coachId && !!date && typeof duration === 'number' && duration >= 5 && duration <= 600

  const onCheck = () => {
    if (!canCheck) return
    setLoading(true)
    setError('')
    setResult(undefined)
    setCopied(false)
    getSlotMatch({ coachId: coachId as string, clientId, date, duration: duration as number, bookedBy })
      .then((res) => setResult(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Could not compare these calendars'))
      .finally(() => setLoading(false))
  }

  const coachName = useMemo(() => coaches.find((c) => c.value === coachId)?.label.split(' — ')[0], [coaches, coachId])
  const coachTz: DisplayZone | null = result?.coachTimezone || null
  const clientTz: DisplayZone | null = result?.clientTimezone || null
  const sameZone = coachTz && clientTz && coachTz.utcOffset === clientTz.utcOffset

  // Plain-text version of the diagnosis, ready to paste into a support reply.
  const summaryText = () => {
    if (!result) return ''
    const lines = [
      `Availability check: ${clientName || 'client'} with ${coachName || 'coach'} on ${result.date}, ${result.durationMinutes}-minute session (booked by the ${result.bookedBy}).`,
      '',
      ...result.reasons
        .filter((r: any) => r.severity !== 'info')
        .map((r: any) => `- ${r.title}. ${r.detail}`),
    ]
    if (result.slots.length) {
      lines.push('', `Available times (${zoneLabel(clientTz)}):`)
      result.slots.forEach((s: any) => lines.push(`- ${formatTime(s.start, clientTz)} – ${formatTime(s.end, clientTz)}`))
    }
    return lines.join('\n')
  }

  const onCopy = () => {
    navigator.clipboard
      ?.writeText(summaryText())
      .then(() => setCopied(true))
      .catch(() => setCopied(false))
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mt-6">
      <p className="text-xl font-bold">Slot match with a coach</p>
      <p className="text-gray-500 text-sm mb-4">
        Pick a coach, a date and a session length to see the exact slots booking would offer between them, and why
        when there are none.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-end">
        <Select
          label="Coach"
          placeholder={coaches.length ? 'Search coach by name or email' : 'Loading coaches…'}
          searchable
          nothingFound="No coach found"
          data={coaches}
          value={coachId}
          onChange={setCoachId}
        />
        <TextInput label="Date" type="date" value={date} onChange={(e) => setDate(e.currentTarget.value)} />
        <NumberInput
          label="Session length (minutes)"
          min={5}
          max={600}
          step={15}
          value={duration}
          onChange={(v) => setDuration(v)}
        />
        <div>
          <p className="text-sm font-medium mb-1">Who is booking?</p>
          <SegmentedControl
            fullWidth
            value={bookedBy}
            onChange={(v) => setBookedBy(v as 'client' | 'coach')}
            data={[
              { value: 'client', label: 'Client books' },
              { value: 'coach', label: 'Coach books' },
            ]}
          />
        </div>
      </div>
      <p className="text-gray-400 text-xs mt-2">
        "Who is booking" matters: when the client books, the coach's working hours apply. When the coach books, the
        client's availability applies.
      </p>

      <Button className="mt-4" color="cta" disabled={!canCheck || loading} onClick={onCheck}>
        Check slots
      </Button>

      {loading && (
        <div className="flex items-center gap-2 text-gray-500 mt-4">
          <Loader size="sm" /> Reading both calendars…
        </div>
      )}
      {error && (
        <Alert color="red" className="mt-4">
          {error}
        </Alert>
      )}

      {result && !loading && (
        <div className="flex flex-col gap-5 mt-6">
          {/* Both sides at a glance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { who: 'Coach', name: coachName, side: result.coach },
              { who: 'Client', name: clientName, side: result.client },
            ].map(({ who, name, side }) => (
              <div key={who} className="border rounded-lg p-4">
                <p className="font-bold">
                  {who}: {name}
                </p>
                <p className="text-gray-600 text-sm">
                  Timezone: {zoneLabel(side.timezone)} (UTC{side.timezone.utcOffset})
                </p>
                <p className="text-gray-600 text-sm">
                  {side.dayName} hours:{' '}
                  {side.workingHours.source === 'not-set'
                    ? 'not set'
                    : side.workingHours.source === 'day-not-enabled'
                    ? 'day switched off'
                    : `${side.workingHours.from} – ${side.workingHours.to}`}
                </p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {side.connectedCalendars.length === 0 && (
                    <span className="text-gray-500 text-sm">No calendar connected</span>
                  )}
                  {side.connectedCalendars.map((c: any) => (
                    <Badge key={c.provider} color={c.fetched === false ? 'red' : sourceColor[c.provider]}>
                      {providerLabel[c.provider]}
                      {c.fetched === false ? ' — not syncing' : ''}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Diagnosis */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="font-bold">Result</p>
              <Button variant="subtle" size="xs" onClick={onCopy}>
                {copied ? 'Copied' : 'Copy reply text'}
              </Button>
            </div>
            <div className="flex flex-col gap-2">
              {result.reasons.map((r: any, idx: number) => (
                <Alert key={idx} color={severityStyle[r.severity]?.color} title={r.title}>
                  {r.detail}
                </Alert>
              ))}
            </div>
          </div>

          {/* Slots */}
          {result.slots.length > 0 && (
            <div>
              <p className="font-bold mb-2">Available slots ({result.slots.length})</p>
              <div className="overflow-x-auto">
                <div
                  className="grid gap-x-6 gap-y-1 text-sm"
                  style={{ gridTemplateColumns: `repeat(${sameZone ? 2 : 3}, max-content)` }}
                >
                  <span className="text-gray-500">Client time ({zoneLabel(clientTz)})</span>
                  {!sameZone && <span className="text-gray-500">Coach time ({zoneLabel(coachTz)})</span>}
                  <span className="text-gray-500">My time</span>
                  {result.slots.map((s: any) => (
                    <Fragment key={s.start}>
                      <span className="font-mono">
                        {dayTime(s.start, clientTz)} – {formatTime(s.end, clientTz)}
                      </span>
                      {!sameZone && (
                        <span className="font-mono">
                          {dayTime(s.start, coachTz)} – {formatTime(s.end, coachTz)}
                        </span>
                      )}
                      <span className="font-mono">
                        {dayTime(s.start, null)} – {formatTime(s.end, null)}
                      </span>
                    </Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* What's blocking */}
          {result.blockingItems.length > 0 && (
            <div>
              <p className="font-bold mb-2">What's taking up the shared window ({result.blockingItems.length})</p>
              <div className="flex flex-col divide-y">
                {result.blockingItems.map((i: any, idx: number) => (
                  <div key={idx} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                    <Badge color={i.owner === 'coach' ? 'violet' : 'cyan'}>{i.owner}</Badge>
                    <span className="font-mono">
                      {i.allDay
                        ? 'All day'
                        : `${dayTime(i.start, i.owner === 'coach' ? coachTz : clientTz)} – ${formatTime(
                            i.end,
                            i.owner === 'coach' ? coachTz : clientTz
                          )}`}
                    </span>
                    <span className="flex-1 min-w-0 break-words">{i.title}</span>
                    <Badge color={sourceColor[i.source]} variant="light">
                      {providerLabel[i.source]}
                    </Badge>
                    {i.markedFree && (
                      <Badge color="orange" variant="light">
                        Marked "free" — still blocks
                      </Badge>
                    )}
                    {i.cancelled && (
                      <Badge color="orange" variant="light">
                        Cancelled — still blocks
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-400 text-xs mt-1">Times shown in each owner's own timezone.</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SlotMatchChecker
